/**
 * HomeworkRoute — `/lekser` board (Fase 4).
 *
 * The dedicated homework surface (decision: own route, not a pool filter).
 * Homework is excluded from the chore pool server-side (queryPool), so this is
 * the only place lekser are discovered. Cards are grouped by subject and, within
 * each group, ordered by the server's due-date sort (soonest first, nulls last);
 * null-subject homework collects under a "Uten fag" group rendered last.
 *
 * Behaviour mirrors PoolBoardRoute: useChoresWS() keeps the board live, and the
 * shared useChoreActions().claim/complete drive the same server-authoritative
 * claim + optimistic complete flow (homework runs the full gamification loop).
 *
 * #154 (redesign steg 4b): toppen er ScreenHeader med «Lekser», tilbakeknapp
 * til Oppgaver og avataren, og kortene er TaskRow-rader som på tavla. Her
 * flytter et tatt kort seg aldri: lista er gruppert etter fag, ikke etter hvem
 * som har kortet, så det blir stående og viser «Fullfør».
 */
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ScreenHeader } from '../../components/ScreenHeader';
import { EmptyState } from '../../components/primitives';
import { toast } from '../../lib/toast';
import { HomeworkCard } from './HomeworkCard';
import { SectionLabel } from '../../components/primitives/SectionLabel';
import type { Viewer } from './ChoreCard';
import { useChoreActions } from './useChoreActions';
import { useChoresWS } from './useChoresWS';
import { useHomework, type HomeworkInstance } from './useHomeworkQuery';

type SessionMember = {
  id: string;
  short_name: string;
  birth_date?: string;
};

function ageYearsAt(birthIso: string | undefined, atIso: string): number {
  if (!birthIso) return Number.POSITIVE_INFINITY;
  const birth = new Date(birthIso);
  const at = new Date(atIso);
  let years = at.getUTCFullYear() - birth.getUTCFullYear();
  const m = at.getUTCMonth() - birth.getUTCMonth();
  if (m < 0 || (m === 0 && at.getUTCDate() < birth.getUTCDate())) years -= 1;
  return years;
}

function useViewer(): Viewer | null {
  const [viewer, setViewer] = useState<Viewer | null>(null);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await globalThis.fetch('/api/auth/me', { credentials: 'same-origin' });
        if (!r.ok) return;
        const body = (await r.json()) as { member?: SessionMember };
        if (cancelled || !body.member) return;
        setViewer({
          id: body.member.id,
          shortName: body.member.short_name,
          ageYears: ageYearsAt(body.member.birth_date, new Date().toISOString()),
        });
      } catch {
        // Network failure — leave viewer null; route renders loading/empty.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);
  return viewer;
}

interface SubjectGroup {
  /** null subject collected under one group (rendered last). */
  subject: string | null;
  items: HomeworkInstance[];
}

/** Group homework by subject, preserving the server's due-date order within
 *  each group and across first-seen subjects; null-subject group goes last. */
function groupBySubject(rows: HomeworkInstance[]): SubjectGroup[] {
  const order: (string | null)[] = [];
  const bySubject = new Map<string | null, HomeworkInstance[]>();
  for (const r of rows) {
    const key = r.subject ?? null;
    const list = bySubject.get(key);
    if (list) list.push(r);
    else {
      bySubject.set(key, [r]);
      order.push(key);
    }
  }
  // Stable ordering with the null ("Uten fag") group always last.
  order.sort((a, b) => (a === null ? 1 : b === null ? -1 : 0));
  return order.map((subject) => ({ subject, items: bySubject.get(subject)! }));
}

export function HomeworkRoute(): ReactNode {
  const { t } = useTranslation();
  useChoresWS();
  const viewer = useViewer();
  const homework = useHomework();
  const actions = useChoreActions();

  const rows = useMemo(() => homework.data ?? [], [homework.data]);
  const groups = useMemo(() => groupBySubject(rows), [rows]);
  const count = rows.length;

  return (
    <main data-testid="homework-board" className="min-h-dvh px-5 pt-4 pb-6 md:px-8">
      <ScreenHeader
        title={t('homework.title')}
        back={{ to: '/oppgavebunken', label: t('homework.backToPool'), testId: 'homework-to-pool' }}
        className="mb-4"
      />

      {count === 0 ? (
        <section data-testid="homework-board-empty">
          <EmptyState
            variant="page"
            heading={t('homework.empty.heading')}
            body={t('homework.empty.body')}
          />
        </section>
      ) : (
        <div data-testid="homework-board-groups" className="flex flex-col gap-5">
          {groups.map((g) => (
            <section key={g.subject ?? '__none__'} data-testid="homework-subject-group">
              <SectionLabel>{g.subject ?? t('homework.noSubject')}</SectionLabel>
              <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
                {g.items.map((c) => (
                  <HomeworkCard
                    key={c.id}
                    instance={c}
                    viewer={viewer ?? { id: 'anonymous', ageYears: 0 }}
                    spinner={actions.claim.isPending && actions.claim.variables === c.id}
                    onClaim={() => {
                      if (!viewer) return;
                      actions.claim.mutate(c.id);
                    }}
                    onComplete={() => {
                      if (!viewer) return;
                      // This surface has no sheet, so a toast IS the visible
                      // error here — the hook stopped toasting complete (#74).
                      actions.complete.mutate(
                        { instanceId: c.id },
                        {
                          onError: () =>
                            toast({ kind: 'error', body: t('chores.complete.error') }),
                        },
                      );
                    }}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </main>
  );
}
